import { OptionType } from '@evinced-private/ui-common';

import { IWebSdkTestIssuesFilterOption } from 'src/common/helpers/DataFilterHelper';
import { buildRunIdParam } from 'src/common/helpers/GQLRequestsHelper/buildRunIdParam';
import { SummaryItem } from 'src/common/interfaces/SummaryItem';
import { gqlClient } from 'src/common/services/api/GraphQLRequestApi';
import { Logger } from 'src/common/services/Logger';

import {
	IGetWebSdkTestIssuesParams,
	IGetWebSdkTestsParams,
	IWebSdkTestBaseData,
	IWebSdkTestDetails,
	TWebSdkTestIssuesResult,
	TWebSdkTestsResult
} from '../types/WebSdkDashboardServiceTypes';

import { buildIssuesVariables } from './queries/Issues/helpers/buildVariables';
import { transformIssuesResults } from './queries/Issues/helpers/transformResults';
import { IssuesQuery } from './queries/Issues/Issues.generated';
import { Issues } from './queries/Issues/Issues.gql';
import { transformIssuesFilterOptionsResults } from './queries/IssuesFilterOptions/helpers/transformResults';
import { IssuesFilterOptionsQuery } from './queries/IssuesFilterOptions/IssuesFilterOptions.generated';
import { IssuesFilterOptions } from './queries/IssuesFilterOptions/IssuesFilterOptions.gql';
import { transformTestBaseDataResults } from './queries/TestBaseData/helpers/transformResults';
import { TestBaseDataQuery } from './queries/TestBaseData/TestBaseData.generated';
import { TestBaseData } from './queries/TestBaseData/TestBaseData.gql';
import { transformTestDetailsResults } from './queries/TestDetails/helpers/transformResults';
import { TestDetailsQuery } from './queries/TestDetails/TestDetails.generated';
import { TestDetails } from './queries/TestDetails/TestDetails.gql';
import { transformTestLabelsResults } from './queries/TestLabels/helpers/transformResults';
import { TestLabelsQuery } from './queries/TestLabels/TestLabels.generated';
import { TestLabels } from './queries/TestLabels/TestLabels.gql';
import {
	buildTestsQueryVariables,
	buildTestsQueryVariablesV2
} from './queries/Tests/helpers/buildVariables';
import { transformTestsResults } from './queries/Tests/helpers/transformResults';
import { TestsQuery } from './queries/Tests/Tests.generated';
import { RecentTests, Tests } from './queries/Tests/Tests.gql';
import {
	buildUsageDataVariables,
	buildUsageDataVariablesV2
} from './queries/UsageData/helpers/buildVariables';
import { transformUsageDataResults } from './queries/UsageData/helpers/transformResults';
import { UsageDataQuery } from './queries/UsageData/UsageData.generated';
import { RecentUsageData, UsageData } from './queries/UsageData/UsageData.gql';

export const getWebSdkUsageData = async (
	isRecentTests: boolean,
	daysFilter?: OptionType,
	searchValue?: string,
	labels?: OptionType[]
): Promise<SummaryItem[]> => {
	try {
		const testsVariables = buildUsageDataVariables(daysFilter, searchValue, labels);

		const query = isRecentTests ? RecentUsageData : UsageData;
		const result = await gqlClient.request<UsageDataQuery>(query, testsVariables);

		return transformUsageDataResults(result, daysFilter);
	} catch (e) {
		Logger.error('WebSdkDashboardService -> getWebSdkUsageData failed with an error:', e);
		throw e;
	}
};

export const getWebSdkUsageDataV2 = async (
	isRecentTests: boolean,
	daysFilter?: OptionType,
	searchValue?: string,
	labels?: OptionType[]
): Promise<SummaryItem[]> => {
	try {
		const testsVariables = buildUsageDataVariablesV2(daysFilter, searchValue, labels);

		const query = isRecentTests ? RecentUsageData : UsageData;
		const result = await gqlClient.request<UsageDataQuery>(query, testsVariables);

		return transformUsageDataResults(result, daysFilter);
	} catch (e) {
		Logger.error('WebSdkDashboardService -> getWebSdkUsageDataV2 failed with an error:', e);
		throw e;
	}
};

export const getWebSdkTests = async ({
	isRecentTests,
	...restParams
}: IGetWebSdkTestsParams): Promise<TWebSdkTestsResult> => {
	try {
		const variables = buildTestsQueryVariables(restParams);

		const query = isRecentTests ? RecentTests : Tests;
		const result = await gqlClient.request<TestsQuery>(query, variables);

		return transformTestsResults(result);
	} catch (e) {
		Logger.error('WebSdkDashboardService -> getWebSdkTests failed with an error:', e);
		throw e;
	}
};

export const getWebSdkTestsV2 = async ({
	isRecentTests,
	...restParams
}: IGetWebSdkTestsParams): Promise<TWebSdkTestsResult> => {
	try {
		const variables = buildTestsQueryVariablesV2(restParams);

		const query = isRecentTests ? RecentTests : Tests;
		const result = await gqlClient.request<TestsQuery>(query, variables);

		return transformTestsResults(result);
	} catch (e) {
		Logger.error('WebSdkDashboardService -> getWebSdkTestsV2 failed with an error:', e);
		throw e;
	}
};

export const getWebSdkTestDetails = async (runId: string): Promise<IWebSdkTestDetails> => {
	try {
		const variables = buildRunIdParam(runId);

		const result = await gqlClient.request<TestDetailsQuery>(TestDetails, variables);

		const transformedResult = transformTestDetailsResults(result);
		if (!transformedResult) {
			throw new Error(`Test ${runId} not found.`);
		}
		return transformedResult;
	} catch (e) {
		Logger.error('WebSdkDashboardService -> getWebSdkTestDetails failed with an error:', e);
		throw e;
	}
};

export const getWebSdkTestIssuesFilterOptions = async (
	runId: string
): Promise<Omit<IWebSdkTestIssuesFilterOption, 'searchValue'>> => {
	try {
		const variables = buildRunIdParam(runId);

		const result = await gqlClient.request<IssuesFilterOptionsQuery>(
			IssuesFilterOptions,
			variables
		);

		return transformIssuesFilterOptionsResults(result);
	} catch (e) {
		Logger.error(
			'WebSdkDashboardService -> getWebSdkTestIssuesFilterOptions failed with an error:',
			e
		);
		throw e;
	}
};

export const getWebSdkTestIssues = async (
	params: IGetWebSdkTestIssuesParams
): Promise<TWebSdkTestIssuesResult> => {
	try {
		const variables = buildIssuesVariables(params);

		const result = await gqlClient.request<IssuesQuery>(Issues, variables);

		const transformedResult = transformIssuesResults(result);
		if (!transformedResult) {
			throw new Error(`Test ${params.runId} not found.`);
		}
		return transformedResult;
	} catch (e) {
		Logger.error('WebSdkDashboardService -> getWebSdkTestIssues failed with an error:', e);
		throw e;
	}
};

export const getWebSdkTestLabels = async (): Promise<OptionType[]> => {
	try {
		const result = await gqlClient.request<TestLabelsQuery>(TestLabels);

		return transformTestLabelsResults(result);
	} catch (e) {
		Logger.error('WebSdkDashboardService -> getWebSdkTestLabels failed with an error:', e);
		throw e;
	}
};

export const getWebSdkTestBaseData = async (runId: string): Promise<IWebSdkTestBaseData> => {
	try {
		const variables = buildRunIdParam(runId);

		const result = await gqlClient.request<TestBaseDataQuery>(TestBaseData, variables);

		const transformedResult = transformTestBaseDataResults(result);
		if (!transformedResult) {
			throw new Error(`Test ${runId} not found.`);
		}
		return transformedResult;
	} catch (e) {
		Logger.error('WebSdkDashboardService -> getWebSdkTestBaseData failed with an error:', e);
		throw e;
	}
};
